/**
 * WS5 — curated "2026 pairings" for the website font picker.
 *
 * The picker historically offered only system-font stacks. This module derives
 * a curated pairing list from the canonical design-direction registry
 * (`src/lib/designDirections/directions.ts`) and generates the self-hosted
 * `@font-face` CSS that makes those pairings REAL on the published site — no
 * worker deploy needed, because the block travels inside the theme's
 * `customCss` through the existing save → scopeCSS → publish path (the
 * css-scoper passes `@font-face` through unchanged).
 *
 * The @font-face block is delimited by `WB_FONTS_START` / `WB_FONTS_END`
 * marker comments so it can be upserted/removed without ever touching user CSS
 * outside the markers (see {@link upsertWbFontsBlock}).
 *
 * Only families with a woff2 already in `${assetBase}/shared/fonts/` are
 * self-hostable; a direction whose HEAD face isn't in {@link WB_FONT_FILES} is
 * excluded from the curated list (its body may still fall back to a system
 * stack — the head face is what carries the look). Licensed faces are
 * registered under their licensed NAME but served as a lookalike file
 * (Didot→bodoni-moda, Neue Haas Grotesk→archivo, Georgia→gelasio,
 * Futura→jost) — the same substitution scheme as
 * `src/lib/utils/builder-fonts.ts`. That module's FACES stay the PDF-parity
 * source and are NOT derived from this table; it only loads this table's
 * HONEST families (a face under its own name) on top, never the four
 * lookalikes above — see `WEBFONT_EXCLUDED_FAMILIES` in
 * `src/lib/fonts/builderFontCatalog.ts`. The tile LABEL, however, names the
 * file that is served (`fontDisplayName`), so the picker never advertises a
 * face Veltric does not ship (Kanzlei 05.10., 7.13) — but ONLY for a family
 * the pairing ships an @font-face for (`families`). A body family nothing is
 * served for (Helvetica Neue, Söhne) is resolved by the visitor's OS and
 * keeps its own name (audit R1, K2-09). The CSS names and the stored stacks
 * are untouched.
 */
import {
  DIRECTIONS,
  DIRECTION_IDS,
  fontDisplayName,
} from "@/lib/designDirections/directions";
import { emailFontStack } from "@/lib/designDirections/directionTheme";

export type WbFontFace = {
  /** woff2 basename (no extension) under `${assetBase}/shared/fonts/`. */
  file: string;
  /** css font-weight value — single weight or variable range ("100 900"). */
  weight: string;
  style: "normal" | "italic";
};

const N = "normal" as const;

/**
 * Registered-family → self-hosted face(s). Single entry with a range =
 * variable font; multiple entries = discrete static weights. Keys are the
 * EXACT css family names the directions registry uses.
 */
export const WB_FONT_FILES: Record<string, ReadonlyArray<WbFontFace>> = {
  Fraunces: [{ file: "fraunces", weight: "100 900", style: N }],
  "Hanken Grotesk": [{ file: "hanken-grotesk", weight: "100 900", style: N }],
  "Playfair Display": [
    // Upright-only file; the browser synthesizes italic (same trick as
    // builder-fonts.ts — close enough to the real italic).
    { file: "playfair-display", weight: "400 700", style: N },
    { file: "playfair-display", weight: "400 700", style: "italic" },
  ],
  "Bricolage Grotesque": [
    { file: "bricolage-grotesque", weight: "100 900", style: N },
  ],
  "Cormorant Garamond": [
    { file: "cormorant-garamond", weight: "100 900", style: N },
  ],
  // Licensed didone — served as Bodoni Moda under the licensed name.
  Didot: [{ file: "bodoni-moda", weight: "100 900", style: N }],
  "Work Sans": [{ file: "work-sans", weight: "100 900", style: N }],
  // Licensed grotesk — served as Archivo under the licensed name.
  "Neue Haas Grotesk": [{ file: "archivo", weight: "100 900", style: N }],
  Montserrat: [{ file: "montserrat", weight: "100 900", style: N }],
  Figtree: [{ file: "figtree", weight: "100 900", style: N }],
  // Metric-compatible Gelasio under the Georgia name (PDF-parity scheme).
  Georgia: [
    { file: "gelasio-regular", weight: "400", style: N },
    { file: "gelasio-bold", weight: "700", style: N },
  ],
  Inter: [
    { file: "inter-regular", weight: "400", style: N },
    { file: "inter-bold", weight: "700", style: N },
  ],
  Poppins: [
    { file: "poppins-regular", weight: "400", style: N },
    { file: "poppins-semibold", weight: "600", style: N },
    { file: "poppins-bold", weight: "700", style: N },
  ],
  Lora: [
    { file: "lora-regular", weight: "400", style: N },
    { file: "lora-bold", weight: "700", style: N },
    { file: "lora-italic", weight: "400", style: "italic" },
  ],
  // App-builder picker families (appStyleTokens.ts STYLE_FONTS) that the
  // website pairings never needed. Latin subset, from the @fontsource builds
  // of the upstream OFL faces — see NOTICE-builder-fonts.txt.
  Roboto: [{ file: "roboto", weight: "100 900", style: N }],
  "Open Sans": [{ file: "open-sans", weight: "300 800", style: N }],
  Merriweather: [{ file: "merriweather", weight: "300 900", style: N }],
  Lato: [
    { file: "lato-regular", weight: "400", style: N },
    { file: "lato-bold", weight: "700", style: N },
  ],
  // Mail / document builder picker families (builderFontCatalog.ts
  // HOSTED_CATALOG). Latin subset from the @fontsource 5.3.0 builds, each
  // under its OWN family name — see NOTICE-builder-fonts.txt for the
  // licences (Roboto Slab and Permanent Marker are Apache-2.0, the rest OFL).
  // The weight ranges are the files' real `wght` axes: the inspector offers
  // exactly these, so a weight it lists is a weight the face can draw.
  "Roboto Slab": [{ file: "roboto-slab", weight: "100 900", style: N }],
  Bitter: [{ file: "bitter", weight: "100 900", style: N }],
  Cabin: [{ file: "cabin", weight: "400 700", style: N }],
  Nunito: [{ file: "nunito", weight: "200 1000", style: N }],
  Oswald: [{ file: "oswald", weight: "200 700", style: N }],
  "Source Sans 3": [{ file: "source-sans-3", weight: "200 900", style: N }],
  "Noto Serif": [{ file: "noto-serif", weight: "100 900", style: N }],
  "Abril Fatface": [{ file: "abril-fatface", weight: "400", style: N }],
  "Permanent Marker": [{ file: "permanent-marker", weight: "400", style: N }],
  Oxygen: [
    { file: "oxygen-regular", weight: "400", style: N },
    { file: "oxygen-bold", weight: "700", style: N },
  ],
  // The PDF-parity mono. It used to exist only in builder-fonts.ts FACES, so
  // `mirror-wb-assets --check` (which reads THIS table) never looked for it.
  "IBM Plex Mono": [
    { file: "ibm-plex-mono-regular", weight: "400", style: N },
    { file: "ibm-plex-mono-bold", weight: "700", style: N },
  ],
  // Jost under the Futura name (PDF-parity scheme).
  Futura: [
    { file: "jost-regular", weight: "400", style: N },
    { file: "jost-bold", weight: "700", style: N },
    { file: "jost-black", weight: "900", style: N },
  ],
};

export type WbFontPairing = {
  /** Tile LABELS — the face the visitor actually gets: for a family in
   *  `families` the woff2 served under it (`fontDisplayName`: "Jost" for
   *  Futura, "Bodoni Moda" for Didot, "Archivo" for Neue Haas Grotesk), for a
   *  family nothing is served for its own name. Display text only; never a
   *  CSS name. */
  textHeader: string;
  textBody: string;
  /** The registry CSS family names the pairing is built from — the keys of
   *  WB_FONT_FILES / EMAIL_FONT_STACKS and the first family of each stack. */
  headFamily: string;
  bodyFamily: string;
  /** Full css stacks stored in fonts.header / fonts.body. */
  header: string;
  body: string;
  /** Families of this pairing that have a self-hosted face (head always;
   *  body only when it's in WB_FONT_FILES). Feed to {@link wbFontFaceCss}. */
  families: string[];
};

function buildCuratedPairings(): WbFontPairing[] {
  const seen = new Set<string>();
  const out: WbFontPairing[] = [];
  for (const id of DIRECTION_IDS) {
    const d = DIRECTIONS[id];
    const key = `${d.fontHead}|${d.fontBody}`;
    if (seen.has(key)) continue;
    seen.add(key);
    // Only pairings whose HEAD face is self-hostable NOW make the cut.
    if (!WB_FONT_FILES[d.fontHead]) continue;
    const families = [d.fontHead];
    if (d.fontBody !== d.fontHead && WB_FONT_FILES[d.fontBody]) {
      families.push(d.fontBody);
    }
    // A label may name the bundled file only where that file is SERVED: the
    // wb-fonts block carries exactly `families`.
    const label = (family: string) =>
      families.includes(family) ? fontDisplayName(family) : family;
    out.push({
      textHeader: label(d.fontHead),
      textBody: label(d.fontBody),
      headFamily: d.fontHead,
      bodyFamily: d.fontBody,
      // The registry stacks lead with the true face and fall back gracefully
      // (shared with email/doc so a family renders the same look everywhere).
      header: emailFontStack(d.fontHead).stack,
      body: emailFontStack(d.fontBody).stack,
      families,
    });
  }
  return out;
}

/** Deduped {head, body} pairings from the design-direction registry, in
 *  registry order, head face self-hostable. */
export const CURATED_FONT_PAIRINGS: ReadonlyArray<WbFontPairing> =
  buildCuratedPairings();

/** woff2 basename → the real name of the face in that file ("jost-bold" →
 *  "Jost", "bodoni-moda" → "Bodoni Moda"). Derived, so a file added to
 *  {@link WB_FONT_FILES} is named without a second list. */
const WB_FACE_NAME_BY_FILE: ReadonlyMap<string, string> = new Map(
  Object.entries(WB_FONT_FILES).flatMap(([family, faces]) =>
    faces.map((f) => [f.file, fontDisplayName(family)] as const),
  ),
);

/**
 * CSS family (lowercased) → the bundled face a customCss string SERVES under
 * it: every `@font-face` whose `src` is one of our woff2 under
 * `/shared/fonts/`. Marker-agnostic on purpose — besides the picker's own
 * wb-fonts block, every event template carries its faces in its own css
 * (`event-templates/*.ts` `fontFaces([...])`: webinar serves gelasio under
 * 'Georgia', corporate bodoni-moda under 'Bodoni 72' and lora under 'Adobe
 * Caslon Pro'), and those stay when the wb-fonts block is removed.
 *
 * A face from any other url is somebody's own file under their own name and
 * is not named here. Pure.
 */
export function servedWbFaceNames(
  customCss: string | null | undefined,
): Map<string, string> {
  const served = new Map<string, string>();
  for (const block of (customCss ?? "").matchAll(/@font-face\s*\{([^}]*)\}/gi)) {
    const body = block[1] ?? "";
    const family = /font-family\s*:\s*(["']?)([^;"']+)\1/i.exec(body)?.[2]?.trim();
    const file = /\/shared\/fonts\/([a-z0-9-]+)\.woff2/i.exec(body)?.[1];
    const face = file ? WB_FACE_NAME_BY_FILE.get(file.toLowerCase()) : undefined;
    if (family && face) served.set(family.toLowerCase(), face);
  }
  return served;
}

/**
 * The label rule of a website font control for a customCss string: the
 * bundled face the css serves under the stack's first family ("Jost" for
 * `Futura, sans-serif` while jost-*.woff2 stands under 'Futura'), else that
 * family's own name — nothing served, the visitor's OS resolves the name the
 * stack asks for. FontBody's off-catalogue tile and every `FontTypeSettings`
 * of the website builder follow it (audit R2 of Kanzlei 05.10., 7.13).
 * Display only. Pure.
 */
export function wbServedFontLabel(
  customCss: string | null | undefined,
): (stack: string) => string {
  const served = servedWbFaceNames(customCss);
  return (stack) => {
    const family =
      stack.split(",")[0]?.trim().replace(/^['"]|['"]$/g, "") || stack;
    return served.get(family.toLowerCase()) ?? family;
  };
}

export const WB_FONTS_START = "/* wb-fonts */";
export const WB_FONTS_END = "/* /wb-fonts */";

/** Matches a whole marker-delimited block (lazy, so multiple blocks match
 *  individually), including its own trailing newline. Fresh regex per use —
 *  the `g` flag makes lastIndex stateful. */
const wbFontsBlockRe = () =>
  /[\t ]*\/\* wb-fonts \*\/[\s\S]*?\/\* \/wb-fonts \*\/[\t ]*(?:\r?\n)?/g;

/**
 * `@font-face` declarations (font-display: swap, ABSOLUTE urls under
 * `${assetBase}/shared/fonts/`) for every self-hostable family in `families`.
 * Returns null when nothing is hostable or `assetBase` is unset (a relative
 * "undefined/…" url on the published site would 404 — better to let the
 * fallback stack stand).
 */
export function wbFontFaceCss(
  families: ReadonlyArray<string>,
  assetBase: string | undefined,
): string | null {
  if (!assetBase) return null;
  const seen = new Set<string>();
  const blocks: string[] = [];
  for (const family of families) {
    if (seen.has(family)) continue;
    seen.add(family);
    const faces = WB_FONT_FILES[family];
    if (!faces) continue;
    for (const f of faces) {
      blocks.push(`@font-face {
  font-family: '${family}';
  font-style: ${f.style};
  font-weight: ${f.weight};
  font-display: swap;
  src: url("${assetBase}/shared/fonts/${f.file}.woff2") format("woff2");
}`);
    }
  }
  return blocks.length ? blocks.join("\n") : null;
}

/**
 * Upsert (or remove) the marker-delimited wb-fonts block in a customCss
 * string. Pure.
 *
 * - `fontFaceCss` set → every existing wb-fonts block is stripped and ONE
 *   fresh block is appended at the end (never duplicated).
 * - `fontFaceCss` null/empty → all wb-fonts blocks are removed.
 *
 * User CSS outside the markers is preserved verbatim; only trailing
 * whitespace (the separator the previous insert added) is trimmed.
 */
export function upsertWbFontsBlock(
  customCss: string,
  fontFaceCss: string | null,
): string {
  const base = (customCss ?? "").replace(wbFontsBlockRe(), "");
  const trimmedBase = base.replace(/\s+$/, "");
  if (!fontFaceCss) return trimmedBase;
  const block = `${WB_FONTS_START}\n${fontFaceCss}\n${WB_FONTS_END}\n`;
  return trimmedBase === "" ? block : `${trimmedBase}\n\n${block}`;
}
